import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  mobileMenuOpen,
  setMobileMenuOpen
}: {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="41790" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-[#111111] animate-pulse rounded-none" />)}
      </div>;
  }
  return <>
      {}
      <div className="hidden md:flex items-center gap-8">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-8">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="font-sans text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA] transition-all hover:text-[#3B82F6] hover:underline hover:decoration-[#D4AF37] hover:underline-offset-4">
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex items-center gap-6 ml-4 border-l border-[#1A1A1A] pl-8">
          <span className="font-sans text-[11px] font-medium text-[#A1A1AA]">
            trader@unshaken.io
          </span>
          <Button variant="outline" className="h-7 px-3 rounded-none border-[#1A1A1A] bg-transparent text-[10px] font-bold uppercase text-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-[#060606] transition-colors">
            Logout
          </Button>
        </div>
      </div>

      {}
      <div className={cn("fixed inset-0 top-[60px] z-40 bg-[#060606] p-6 transition-transform duration-300 ease-in-out md:hidden", mobileMenuOpen ? "translate-x-0" : "translate-x-full")}>
        <nav className="flex flex-col space-y-6">
          {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-sans text-lg font-bold uppercase tracking-tight text-[#F4F4F5] border-b border-[#1A1A1A] pb-2" onClick={() => setMobileMenuOpen(false)}>
              {item.label}
            </Link>)}
          <div className="mt-auto pt-8 flex flex-col gap-4">
            <span className="font-sans text-sm text-[#A1A1AA]">trader@unshaken.io</span>
            <Button className="w-full rounded-none bg-[#F4F4F5] text-[#060606] font-bold uppercase py-6" onClick={() => setMobileMenuOpen(false)}>
              Logout
            </Button>
          </div>
        </nav>
      </div>
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full bg-[#060606] transition-all duration-300 border-b border-[#1A1A1A]", isScrolled && "bg-[#060606]/80 backdrop-blur-lg shadow-2xl")}>
      <div className="container mx-auto px-6 md:px-12 h-[60px] flex items-center justify-between">
        {}
        <Link to="/DashboardPage" className="z-50">
          <span className="font-sans text-sm font-bold text-[#F4F4F5] tracking-[2px] uppercase">
            The Unshaken Protocol
          </span>
        </Link>

        {}
        <MenuProvider menu_id="16" className="flex items-center">
          <HeaderMenuConsumer mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        </MenuProvider>

        {}
        <Button variant="ghost" size="icon" className="md:hidden z-50 text-[#F4F4F5] hover:bg-[#111111]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>
    </header>;
}